import React, { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { searchPeopleSelector } from 'src/components/ui/form/peoplePicker/selectors';
import { searchPeople } from 'src/components/ui/form/peoplePicker/actions';
import { selectUsers } from 'src/modules/users/selector';
import Loading from 'src/components/Loading';
import { UserListItem } from 'src/types/main';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface UserData {
  avatar: string | null;
  shortName: string;
  displayName: string;
  email: string;
}

interface UsersState {
  [email: string]: UserData;
}

type SearchPeopleItem = {
  id?: number;
  value: string;
  label: string;
  avatar: string | null;
};

interface SingleUserSelectProps {
  selectedUser: any;
  onUserSelect: (user: UserListItem | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export const SingleUserSelect = ({ selectedUser, onUserSelect, label, required = false, disabled = false }: SingleUserSelectProps) => {
  const [searchString, setSearchString] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const users: UsersState = useAppSelector(selectUsers);
  const people: SearchPeopleItem[] = useAppSelector(searchPeopleSelector);
  const status = useAppSelector((state) => state.searchUsers.status);

  useEffect(() => {
    if (searchString && searchString.length >= 3) {
      dispatch(searchPeople(searchString));
    }
  }, [searchString, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUserSelect = (person: SearchPeopleItem) => {
    if (disabled) return;
    const newUser: UserListItem = {
      id: person.id,
      email: person.value,
      title: person.label,
      avatar: person.avatar,
      shortName: users[person.value]?.shortName,
    };
    onUserSelect(newUser);
    setIsFocused(false);
    setSearchString('');
  };

  const handleClearUser = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    onUserSelect(null);
    setSearchString('');
  };

  const renderUserAvatar = (avatar: string | null, shortName: string, displayName: string) => {
    if (avatar) {
      return <img src={avatar} alt={displayName} className='w-8 h-8 rounded-full object-cover' />;
    }
    return <div className='w-8 h-8 rounded-full bg-[#012169] flex items-center justify-center text-sm font-medium text-white'>{shortName}</div>;
  };

  const showDropdown = isFocused && !selectedUser && !disabled && searchString.length >= 3;

  return (
    <div className='flex flex-col gap-1'>
      <label className='text-black text-base flex justify-between items-center'>
        {label}
        {required && <span className='text-[#BA0C2F]'>*</span>}
      </label>

      <div className='relative' ref={containerRef}>
        {selectedUser ? (
          <div
            className={`w-full text-base rounded-md border px-5 outline-none shadow-custom min-h-[48px] flex items-center gap-2 ${
              disabled ? 'bg-[#E5E7EB] text-[#6B7280] border-[#E5E7EB]' : 'bg-white text-black border-[#DFE4EA]'
            }`}
          >
            <div className='flex items-center justify-between w-full'>
              <div className='flex items-center gap-2'>
                {renderUserAvatar(users[selectedUser.email]?.avatar, users[selectedUser.email]?.shortName, users[selectedUser.email]?.displayName)}
                <span className='text-base'>{selectedUser.title}</span>
              </div>
              {!disabled && (
                <button onClick={handleClearUser} className='text-gray-400 hover:text-gray-600'>
                  <XMarkIcon className='w-6 h-6' />
                </button>
              )}
            </div>
          </div>
        ) : (
          <input
            type='text'
            value={searchString}
            placeholder='Выберите ответственного'
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onChange={(e) => setSearchString(e.target.value)}
            className={`w-full text-base rounded-md border px-5 outline-none shadow-custom min-h-[48px] placeholder:text-gray-400 ${
              disabled
                ? 'bg-[#E5E7EB] text-[#6B7280] border-[#E5E7EB] cursor-not-allowed'
                : 'bg-white text-black border-[#DFE4EA]'
            }`}
          />
        )}

        {showDropdown && (
          <div className='absolute z-[101] top-full left-0 right-0 mt-1 bg-white border border-[#DFE4EA] rounded-md shadow-lg max-h-60 overflow-y-auto'>
            {status === 'LOADING' ? (
              <div className='flex items-center justify-center p-4'>
                <Loading />
              </div>
            ) : (
              <div className='p-2'>
                {people.map((person) => (
                  <div key={person.value} className='flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer' onClick={() => handleUserSelect(person)}>
                    {renderUserAvatar(users[person.value]?.avatar, users[person.value]?.shortName, users[person.value]?.displayName)}
                    <span className='text-sm'>{person.label}</span>
                  </div>
                ))}
                {status === 'SUCCESS' && people.length === 0 && (
                  <div className='p-4 text-center text-gray-500'>
                    Нет совпадений по запросу <strong>{searchString}</strong>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
